import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Chip,
} from "@mui/material";
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import { BASE_URL } from "../../../services/configUrls";
import AddNewFeatureModal from "./AddNewFeatureModal";
import CreateTextStatusModal from "./CreateTextStatusModal";

const AddFeatureModal = ({
  open,
  membershipId,
  existingFeatures,
  onClose,
  onFeaturesAdded,
  setSnackbar,
}) => {
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [featureTypes, setFeatureTypes] = useState({});
  const [featureStatusValues, setFeatureStatusValues] = useState({});
  const [featureSearchQuery, setFeatureSearchQuery] = useState("");
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [submittingFeatures, setSubmittingFeatures] = useState(false);
  const [apiStatusOptions, setApiStatusOptions] = useState([]);

  // States for Add New Feature Modal
  const [openAddNewFeatureDialog, setOpenAddNewFeatureDialog] = useState(false);
  const [newFeatureNameInput, setNewFeatureNameInput] = useState("");
  const [newFeatureTypeInput, setNewFeatureTypeInput] = useState("text");

  // States for Create Text Status Modal
  const [openCreateTextStatusDialog, setOpenCreateTextStatusDialog] =
    useState(false);
  const [newStatusText, setNewStatusText] = useState("");
  const [newStatusSymbol, setNewStatusSymbol] = useState("");
  const [currentEditingFeature, setCurrentEditingFeature] = useState(null);

  useEffect(() => {
    if (open) {
      fetchAvailableFeatures();
      fetchStatusOptions();
    } else {
      // Reset state when modal closes
      setSelectedFeatures([]);
      setFeatureTypes({});
      setFeatureStatusValues({});
      setFeatureSearchQuery("");
    }
  }, [open]);

  const fetchAvailableFeatures = async () => {
    setLoadingFeatures(true);
    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(`${BASE_URL}/accounts/all-feature`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const result = await response.json();

      if (result && Array.isArray(result)) {
        setAvailableFeatures(result);
      }
    } catch (error) {
      console.error("Error fetching features:", error);
    } finally {
      setLoadingFeatures(false);
    }
  };

  const fetchStatusOptions = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(`${BASE_URL}/accounts/all-status-options`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const result = await response.json();

      if (Array.isArray(result)) {
        const transformedStatuses = result.map((status) => ({
          id: status.id,
          text: status.status_text,
          symbol: status.symbol,
          color: getColorForSymbol(status.symbol),
        }));
        setApiStatusOptions(transformedStatuses);
      }
    } catch (error) {
      console.error("Error fetching status options:", error);
    }
  };

  const getColorForSymbol = (symbol) => {
    switch (symbol) {
      case "✓":
        return { bg: "#D1FAE5", color: "#065F46" };
      case "✗":
        return { bg: "#F3F4F6", color: "#374151" };
      case "*":
        return { bg: "#DBEAFE", color: "#1E40AF" };
      default:
        return { bg: "#E0E7FF", color: "#3730A3" };
    }
  };

  const handleFeatureToggle = (featureName) => {
    setSelectedFeatures((prev) => {
      const newSelection = prev.includes(featureName)
        ? prev.filter((name) => name !== featureName)
        : [...prev, featureName];

      if (!newSelection.includes(featureName)) {
        setFeatureTypes((prevTypes) => {
          const newTypes = { ...prevTypes };
          delete newTypes[featureName];
          return newTypes;
        });
        setFeatureStatusValues((prevValues) => {
          const newValues = { ...prevValues };
          delete newValues[featureName];
          return newValues;
        });
      } else if (!featureTypes[featureName]) {
        const feature = availableFeatures.find(
          (f) => f.feature_name === featureName
        );
        setFeatureTypes((prevTypes) => ({
          ...prevTypes,
          [featureName]: feature?.type.toLowerCase() || "text",
        }));
        setFeatureStatusValues((prevValues) => ({
          ...prevValues,
          [featureName]: feature?.type === "Count" ? "0" : "TBA",
        }));
      }

      return newSelection;
    });
  };

  const handleFeatureStatusValueChange = (featureName, value) => {
    setFeatureStatusValues((prev) => ({
      ...prev,
      [featureName]: value,
    }));
  };

  const handleAddFeatureBulk = async () => {
    if (selectedFeatures.length === 0 || !membershipId) {
      return;
    }

    setSubmittingFeatures(true);
    try {
      const accessToken = localStorage.getItem("accessToken");

      const featureStatusMappings = selectedFeatures.map((featureName) => {
        const feature = availableFeatures.find(
          (f) => f.feature_name === featureName
        );

        if (feature.type === "Count") {
          return {
            feature_id: feature.feature_id,
            count_value: parseInt(featureStatusValues[featureName] || "0"),
          };
        } else {
          const statusValue = featureStatusValues[featureName] || "TBA";
          const statusOption = apiStatusOptions.find(
            (opt) => opt.text === statusValue
          );

          return {
            feature_id: feature.feature_id,
            status_option_id: statusOption?.id || null,
          };
        }
      });

      const payload = {
        membership_id: membershipId,
        feature_status_mappings: featureStatusMappings,
      };

      const response = await fetch(
        `${BASE_URL}/accounts/membership-feature-status-bulk`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: result.message || "Features added successfully",
          severity: "success",
        });

        if (onFeaturesAdded) {
          await onFeaturesAdded();
        }

        onClose();
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to add features",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error adding features:", error);
      setSnackbar({
        open: true,
        message: "Error adding features. Please try again.",
        severity: "error",
      });
    } finally {
      setSubmittingFeatures(false);
    }
  };

  const handleRefreshAfterFeatureCreated = async () => {
    await fetchAvailableFeatures();
    await fetchStatusOptions();
  };

  const getFilteredAndSortedFeatures = () => {
    const currentFeatureIds = existingFeatures.map((f) => f.feature_id);

    const filtered = availableFeatures.filter(
      (feature) =>
        !currentFeatureIds.includes(feature.feature_id) &&
        feature.feature_name
          .toLowerCase()
          .includes(featureSearchQuery.toLowerCase())
    );

    const selected = [];
    const unselected = [];

    filtered.forEach((feature) => {
      if (selectedFeatures.includes(feature.feature_name)) {
        selected.push(feature);
      } else {
        unselected.push(feature);
      }
    });

    return [...selected, ...unselected];
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={submittingFeatures ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={submittingFeatures}
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 600, pb: 1 }}>
          Select Features
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Search features..."
            value={featureSearchQuery}
            onChange={(e) => setFeatureSearchQuery(e.target.value)}
            disabled={submittingFeatures}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "#9CA3AF", fontSize: 20 }} />
              ),
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
              },
            }}
          />

          <Typography
            variant="caption"
            sx={{
              color: "#6C757D",
              fontSize: "12px",
              mb: 2,
              display: "block",
            }}
          >
            {selectedFeatures.length > 0
              ? `${selectedFeatures.length} selected`
              : "Select features to add"}
          </Typography>

          {loadingFeatures ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box sx={{ maxHeight: 400, overflowY: "auto", pr: 0.5 }}>
              <Button
                fullWidth
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                onClick={() => setOpenAddNewFeatureDialog(true)}
                variant="outlined"
                disabled={submittingFeatures}
                sx={{
                  mb: 2,
                  justifyContent: "flex-start",
                  fontSize: "13px",
                  py: 1,
                  borderColor: "#2D7FF9",
                  color: "#2D7FF9",
                  "&:hover": {
                    backgroundColor: "#F0F7FF",
                    borderColor: "#2D7FF9",
                  },
                }}
              >
                Add New Feature
              </Button>

              {getFilteredAndSortedFeatures().map((feature) => (
                <Box key={feature.feature_id} sx={{ mb: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedFeatures.includes(
                          feature.feature_name
                        )}
                        onChange={() =>
                          handleFeatureToggle(feature.feature_name)
                        }
                        disabled={submittingFeatures}
                        size="small"
                        sx={{
                          color: "#9CA3AF",
                          p: 0.5,
                          "&.Mui-checked": {
                            color: "#2D7FF9",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                        {feature.feature_name}
                      </Typography>
                    }
                    sx={{
                      mx: 0,
                      mb: selectedFeatures.includes(feature.feature_name)
                        ? 1
                        : 0,
                      width: "100%",
                    }}
                  />

                  {selectedFeatures.includes(feature.feature_name) && (
                    <Box sx={{ ml: 4, mb: 1 }}>
                      {feature.type === "Count" ? (
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Count Value"
                          value={
                            featureStatusValues[feature.feature_name] || "0"
                          }
                          onChange={(e) =>
                            handleFeatureStatusValueChange(
                              feature.feature_name,
                              e.target.value
                            )
                          }
                          disabled={submittingFeatures}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              fontSize: "13px",
                            },
                            "& .MuiInputLabel-root": {
                              fontSize: "13px",
                            },
                          }}
                        />
                      ) : (
                        <Autocomplete
                          freeSolo
                          disabled={submittingFeatures}
                          options={apiStatusOptions
                            .sort((a, b) => b.id - a.id)
                            .map((st) => st.text)}
                          value={
                            featureStatusValues[feature.feature_name] || "TBA"
                          }
                          onChange={(event, newValue) => {
                            if (newValue) {
                              handleFeatureStatusValueChange(
                                feature.feature_name,
                                newValue
                              );
                            }
                          }}
                          onInputChange={(event, newInputValue) => {
                            handleFeatureStatusValueChange(
                              feature.feature_name,
                              newInputValue
                            );
                          }}
                          ListboxProps={{
                            style: { maxHeight: "250px", padding: 0 },
                          }}
                          ListboxComponent={React.forwardRef((props, ref) => {
                            const { children, ...other } = props;
                            return (
                              <ul
                                {...other}
                                ref={ref}
                                style={{ padding: 0, margin: 0 }}
                              >
                                {/* Sticky "Create New" option */}
                                <Box
                                  component="li"
                                  onClick={() => {
                                    setCurrentEditingFeature(
                                      feature.feature_name
                                    );
                                    setOpenCreateTextStatusDialog(true);
                                  }}
                                  sx={{
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 10,
                                    backgroundColor: "#FFFFFF",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    padding: "8px 14px",
                                    cursor: "pointer",
                                    borderBottom: "1px solid #E5E7EB",
                                    listStyle: "none",
                                    "&:hover": {
                                      backgroundColor: "#F0F9FF",
                                    },
                                  }}
                                >
                                  <AddIcon
                                    sx={{
                                      fontSize: 16,
                                      color: "#2D7FF9",
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "13px",
                                      color: "#2D7FF9",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Create New...
                                  </Typography>
                                </Box>
                                {/* Scrollable status list */}
                                {children}
                              </ul>
                            );
                          })}
                          renderOption={(props, option) => (
                            <li {...props} key={option}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {apiStatusOptions.find(
                                  (st) => st.text === option
                                )?.symbol && (
                                  <Chip
                                    label={
                                      apiStatusOptions.find(
                                        (st) => st.text === option
                                      )?.symbol || ""
                                    }
                                    size="small"
                                    sx={{
                                      backgroundColor: getColorForSymbol(
                                        apiStatusOptions.find(
                                          (st) => st.text === option
                                        )?.symbol
                                      ).bg,
                                      color: getColorForSymbol(
                                        apiStatusOptions.find(
                                          (st) => st.text === option
                                        )?.symbol
                                      ).color,
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      height: "20px",
                                    }}
                                  />
                                )}
                                <Typography sx={{ fontSize: "13px" }}>
                                  {option}
                                </Typography>
                              </Box>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              label="Status Value"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  fontSize: "13px",
                                },
                                "& .MuiInputLabel-root": {
                                  fontSize: "13px",
                                },
                              }}
                            />
                          )}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              ))}

              {!loadingFeatures &&
                getFilteredAndSortedFeatures().length === 0 &&
                !featureSearchQuery && (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 4,
                      px: 2,
                      backgroundColor: "#F9FAFB",
                      borderRadius: "4px",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#6C757D",
                        fontSize: "13px",
                      }}
                    >
                      All features have been added
                    </Typography>
                  </Box>
                )}

              {!loadingFeatures &&
                getFilteredAndSortedFeatures().length === 0 &&
                featureSearchQuery && (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 4,
                      px: 2,
                      backgroundColor: "#F9FAFB",
                      borderRadius: "4px",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#6C757D",
                        fontSize: "13px",
                      }}
                    >
                      No features found matching "{featureSearchQuery}"
                    </Typography>
                  </Box>
                )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={onClose}
            disabled={submittingFeatures}
            sx={{ fontSize: "13px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddFeatureBulk}
            variant="contained"
            disabled={selectedFeatures.length === 0 || submittingFeatures}
            startIcon={
              submittingFeatures ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AddIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{ fontSize: "13px" }}
          >
            {submittingFeatures
              ? "Adding..."
              : `Add${
                  selectedFeatures.length > 0
                    ? ` (${selectedFeatures.length})`
                    : ""
                }`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Feature Modal */}
      <AddNewFeatureModal
        open={openAddNewFeatureDialog}
        onClose={() => setOpenAddNewFeatureDialog(false)}
        newFeatureNameInput={newFeatureNameInput}
        setNewFeatureNameInput={setNewFeatureNameInput}
        newFeatureTypeInput={newFeatureTypeInput}
        setNewFeatureTypeInput={setNewFeatureTypeInput}
        onFeatureCreated={handleRefreshAfterFeatureCreated}
      />

      {/* Create Text Status Modal */}
      <CreateTextStatusModal
        open={openCreateTextStatusDialog}
        onClose={() => setOpenCreateTextStatusDialog(false)}
        newStatusText={newStatusText}
        setNewStatusText={setNewStatusText}
        newStatusSymbol={newStatusSymbol}
        setNewStatusSymbol={setNewStatusSymbol}
        onRefreshStatusOptions={fetchStatusOptions}
        onShowSnackbar={setSnackbar}
        onResetTempValue={(newValue) => {
          if (newValue && currentEditingFeature) {
            handleFeatureStatusValueChange(currentEditingFeature, newValue);
          }
        }}
      />
    </>
  );
};

export default AddFeatureModal;
